import React, { useState, useEffect } from 'react';
import { getUserAssignedAudits } from '../../services/auditAPI';
// import { Audit } from './types/audit';
import { 
  BarChart3, 
  Search, 
  Filter, 
  AlertCircle,
  CheckCircle2,
  Clock,
  Activity
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { format } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

function App() {
  const [audits, setAudits] = useState([]);
  const [filteredAudits, setFilteredAudits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAudits = async () => {
      const data = await getUserAssignedAudits();
      setAudits(data);
      setFilteredAudits(data);
      setLoading(false);
    };
    fetchAudits();
  }, []);

  useEffect(() => {
    const filtered = audits.filter(audit => {
      const matchesSearch = audit.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          audit.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || audit.status.toLowerCase() === statusFilter;
    
      return matchesSearch && matchesStatus;
    });
    setFilteredAudits(filtered);
  }, [searchTerm, statusFilter, audits]);

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in progress': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const statusData = {
    labels: ['Completed', 'In Progress', 'Pending'],
    datasets: [{
      data: [
        audits.filter(a => a.status.toLowerCase() === 'completed').length,
        audits.filter(a => a.status.toLowerCase() === 'in progress').length,
        audits.filter(a => a.status.toLowerCase() === 'pending').length,
      ],
      backgroundColor: ['#10B981', '#3B82F6', '#F59E0B'],
    }],
  };

  const areaData = {
    labels: [...new Set(audits.map(audit => audit?.area?.name))],
    datasets: [{
      label: 'Audits by Area',
      data: [...new Set(audits.map(audit => audit?.area?.name))].map(area =>
        audits.filter(audit => audit?.area?.name === area).length
      ),
      backgroundColor: '#3B82F6',
    }],
  };

  const stats = [
    {
      title: 'Total Audits',
      value: audits.length,
      icon: <Activity className="w-6 h-6 text-blue-600" />,
    },
    {
      title: 'Completed',
      value: audits.filter(a => a.status.toLowerCase() === 'completed').length,
      icon: <CheckCircle2 className="w-6 h-6 text-green-600" />,
    },
    {
      title: 'In Progress',
      value: audits.filter(a => a.status.toLowerCase() === 'in progress').length,
      icon: <Clock className="w-6 h-6 text-yellow-600" />,
    },
    {
      title: 'Pending',
      value: audits.filter(a => a.status.toLowerCase() === 'pending').length,
      icon: <AlertCircle className="w-6 h-6 text-red-600" />,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center ">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Safety Audit Dashboard</h1>
          <p className="mt-2 text-gray-600">Monitor and manage your safety audits</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="mt-2 text-3xl font-semibold text-gray-900">{stat.value}</p>
                </div>
                {stat.icon}
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Audit Status Distribution</h2>
            <div className="h-64">
              <Doughnut data={statusData} options={{ maintainAspectRatio: false }} />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Audits by Area</h2>
            <div className="h-64">
              <Bar data={areaData} options={{ maintainAspectRatio: false }} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search audits..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="text-gray-400" />
              <select
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="in progress">In Progress</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
        </div>

        {/* Audit List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredAudits.map((audit) => (
              <div key={audit._id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{audit.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(audit.status)}`}>
                    {audit.status}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Area</p>
                    <p className="mt-1">{audit?.area?.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Created</p>
                    <p className="mt-1">{format(new Date(audit.createdAt), 'MMM dd, yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Assigned To</p>
                    <p className="mt-1">{audit.assignedTo.map(user => `${user.userId.firstName} ${user.userId.lastName}`).join(', ')}</p>
                  </div>
                </div>
                <p className="mt-4 text-gray-600">{audit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;